import { reportNames } from "../../../constants/stringConstants";

/**
 * Generate URL and request body for each report that does not use the shared
 * reportCriteria payload.
 *
 * Register every new report here. handleGenerate in report.tsx looks up the
 * selected report value and calls buildPayload. This is the approach for every
 * new report: add an entry in this file instead of a new URL or payload branch
 * in report.tsx.
 *
 * To add a report:
 * 1. Add the report value on reportNames.
 * 2. Add a payload interface for the JSON that report's endpoint expects.
 * 3. Add an entry to DEDICATED_REPORT_ENDPOINTS, keyed by that reportNames
 *    value, with the generate URL and a buildPayload function.
 * 4. buildPayload reads ReportPayloadInput. If the report needs a form field
 *    that is not on ReportPayloadInput, add the field to that interface and
 *    pass it from the buildPayload call in handleGenerate.
 *
 * A report that is not in this map still posts reportCriteria to
 * LEGACY_GENERATE_URL.
 */

/**
 * Form fields shared by every dedicated report builder.
 *
 * This is the snapshot handleGenerate passes in. It is not the JSON posted to
 * the API. Each builder copies the fields its endpoint needs and ignores the
 * rest.
 *
 * If a new report needs a field that is not listed here, add it to this
 * interface and pass it from the buildPayload call in report.tsx.
 */
export interface ReportPayloadInput {
  startDate: string;
  endDate: string;
  selectedAccounts: { value: string | number }[];
  isAllAccounts: boolean;
  activeFilter: boolean;
  inactiveFilter: boolean;
  reportGenType: string;
}

/**
 * JSON body for POST reports/access-codes-used-by-account/generate.
 * accountIds is empty when every account is selected.
 */
export interface AccessCodesUsedByAccountPayload {
  startDate: string;
  endDate: string;
  accountIds: number[];
  allAccountsSelected: boolean;
  active: boolean;
  inactive: boolean;
  reportGenType: string;
}

/** Generate URL and payload builder for one dedicated report. */
interface DedicatedReportEndpoint<P> {
  url: string;
  buildPayload: (input: ReportPayloadInput) => P;
}

/** The report page stores Excel as "xls"; this endpoint expects "xlsx". */
const toApiFileType = (type: string): string => (type === "xls" ? "xlsx" : type);

/**
 * Dedicated reports, keyed by selectedReportType.value (a reportNames value).
 * Add every new report that has its own generate URL and body here.
 */
export const DEDICATED_REPORT_ENDPOINTS: Record<
  string,
  DedicatedReportEndpoint<unknown>
> = {
  [reportNames.accessCodesUsedByAccountValue]: {
    url: "reports/access-codes-used-by-account/generate",
    buildPayload: (input): AccessCodesUsedByAccountPayload => ({
      startDate: input.startDate,
      endDate: input.endDate,
      accountIds: input.isAllAccounts
        ? []
        : input.selectedAccounts.map((account) => Number(account.value)),
      allAccountsSelected: input.isAllAccounts,
      active: input.activeFilter,
      inactive: input.inactiveFilter,
      reportGenType: toApiFileType(input.reportGenType),
    }),
  },
};

/** Generate URL for reports that still send the shared reportCriteria body. */
export const LEGACY_GENERATE_URL = "reports/generate";
